import { useState } from "react";
import { Link } from "react-router-dom";
import { Microphone, VideoCamera } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Panel, Pill } from "@/components/admin/AdminUI";
import { adminMediaUrl, mediaUrl } from "@/lib/api";
import { formatDate, formatDuration } from "@/lib/format";

// Plays the member's current intro through the admin stream (?auth=, byte ranges). Nothing loads until Play (each play is audited).
function Player({ g }) {
  const [failed, setFailed] = useState(false);
  const src = adminMediaUrl(g.stream_url);
  const el = g.type === "video"
    ? <video src={src} controls playsInline preload="none" onError={() => setFailed(true)} className="aspect-[3/4] w-40 shrink-0 rounded-xl bg-black object-contain" data-testid={`glass-video-${g.user_id}`} />
    : <audio src={src} controls preload="none" onError={() => setFailed(true)} className="w-full max-w-xs" data-testid={`glass-audio-${g.user_id}`} />;
  return (
    <div className="space-y-1">
      {el}
      {failed && <p className="cw-error text-xs" role="alert" data-testid={`glass-player-error-${g.user_id}`}>This browser can’t play it. <a href={src} download className="underline">Download the file</a></p>}
    </div>
  );
}

function Actions({ g, onAct }) {
  const id = g.user_id;
  if (g.status === "live") return <Button size="sm" variant="outline" onClick={() => onAct(g, "reject")} data-testid={`glass-take-down-button-${id}`}>Take down</Button>;
  if (g.status === "pending") return (
    <div className="flex gap-2">
      <Button size="sm" onClick={() => onAct(g, "approve")} data-testid={`glass-approve-button-${id}`}>Approve</Button>
      <Button size="sm" variant="outline" onClick={() => onAct(g, "reject")} data-testid={`glass-take-down-button-${id}`}>Take down</Button>
    </div>
  );
  return <Button size="sm" onClick={() => onAct(g, "restore")} data-testid={`glass-put-back-button-${id}`}>Put back live</Button>;
}

export default function GlassRow({ g, onAct }) {
  const m = g.member || {};
  const video = g.type === "video";
  const thumb = mediaUrl(m.photo_thumb || m.photo);
  return (
    <li data-testid={`glass-row-${g.user_id}`}>
      <Panel className="flex flex-col gap-4 p-4 md:flex-row md:items-center">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {thumb ? <img src={thumb} alt="" className="h-11 w-11 shrink-0 rounded-full object-cover" /> : <span className="h-11 w-11 shrink-0 rounded-full bg-[var(--cw-surface-tertiary)]" />}
          <div className="min-w-0 space-y-1">
            <Link to={`/admin/users/${m.id || g.user_id}`} className="block truncate font-medium hover:underline" data-testid={`glass-member-link-${g.user_id}`}>
              {m.first_name || "Member"}{m.age ? `, ${m.age}` : ""}
            </Link>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Pill tone="neutral" data-testid={`glass-type-${g.user_id}`}>{video ? <><VideoCamera size={12} />Video</> : <><Microphone size={12} />Voice</>}</Pill>
              {g.duration_s != null && <span className="cw-soft tabular-nums">{formatDuration(g.duration_s)}</span>}
              <span className="cw-muted" data-testid={`glass-uploaded-${g.user_id}`}>Uploaded {formatDate(g.uploaded_at)}</span>
            </div>
            {g.reason && <p className="cw-warning text-xs" data-testid={`glass-reason-${g.user_id}`}>{g.reason}</p>}
            {g.reviewed_by && <p className="cw-muted text-xs" data-testid={`glass-reviewed-${g.user_id}`}>Reviewed by {g.reviewed_by}, {formatDate(g.reviewed_at)}</p>}
          </div>
        </div>
        <div className="md:w-80"><Player g={g} /></div>
        <div className="shrink-0"><Actions g={g} onAct={onAct} /></div>
      </Panel>
    </li>
  );
}
